/**
 * The money layer.
 *
 * ─── How the economics actually work ────────────────────────────────────────
 *
 * A trader is not paid by the platform. A trader *funds* the platform and is
 * paid a commission for doing so:
 *
 *   1. The trader posts USDT as collateral. It sits in their DEPOSIT account.
 *   2. A merchant creates a payment. The USDT the merchant is owed is FROZEN
 *      out of the trader's deposit before the payment is ever shown to anyone.
 *      A trader without enough free collateral is not routed the payment.
 *   3. The payer sends RUB to the trader's card. The trader keeps that RUB.
 *   4. The trader confirms. The frozen USDT is released to the merchant, and
 *      the trader's commission is credited to their AVAILABLE account.
 *   5. The trader withdraws from AVAILABLE whenever they like.
 *
 * The collateral is the whole security model. If the trader claims the RUB
 * never arrived and an admin finds against them, the frozen USDT still goes to
 * the merchant — the trader has been paid in rubles and simply loses the
 * collateral that covered it. That is what makes a false dispute expensive
 * instead of free, and it is why an admin reviewing screenshots is a backstop
 * rather than the only control.
 *
 * ─── Invariants ─────────────────────────────────────────────────────────────
 *
 * • Every ledger entry is immutable. Corrections are new entries, never edits.
 * • Every movement is balanced: entries created together sum to zero across
 *   accounts, so `SUM(amount)` over a trader's entries is their net position
 *   and can never drift from the sum of their three account balances.
 * • A balance is always `SUM(entries)`. No cached figure is authoritative.
 * • Money never leaves an account that has less than the amount being moved:
 *   the write is conditional on the resulting balance staying >= 0.
 * • Every entry carries an idempotency key, so a retried settlement or a
 *   double-clicked withdrawal cannot post twice.
 */

// ---------- Accounts ----------

export const LedgerAccount = {
  /** Collateral backing live payments. Funded by deposits, drawn by freezes. */
  Deposit: 'deposit',
  /** Collateral committed to payments that have not resolved yet. */
  Frozen: 'frozen',
  /** Earned commission. The only account a trader can withdraw from. */
  Available: 'available',
} as const;
export type LedgerAccount = (typeof LedgerAccount)[keyof typeof LedgerAccount];

export const LEDGER_ACCOUNTS: LedgerAccount[] = Object.values(LedgerAccount);

// ---------- Entry kinds ----------

export const LedgerKind = {
  /** Trader funded their collateral. deposit +N */
  Deposit: 'deposit',
  /** Payment created: collateral committed. deposit −N, frozen +N */
  Freeze: 'freeze',
  /** Payment expired or cancelled: collateral returned. frozen −N, deposit +N */
  Unfreeze: 'unfreeze',
  /** Payment settled: collateral released to the merchant. frozen −N */
  Release: 'release',
  /** Trader's cut of a settled payment. available +N */
  Commission: 'commission',
  /** Dispute resolved against the trader: collateral released anyway. frozen −N */
  DisputeLoss: 'dispute_loss',
  /** Withdrawal approved and sent. available −N */
  Withdrawal: 'withdrawal',
  /** A withdrawal that failed on the way out, returned. available +N */
  WithdrawalReversal: 'withdrawal_reversal',
  DepositWithdrawal: 'deposit_withdrawal',
  DepositWithdrawalReversal: 'deposit_withdrawal_reversal',
  /** Manual correction by an admin. Always carries a reason. */
  Adjustment: 'adjustment',
} as const;
export type LedgerKind = (typeof LedgerKind)[keyof typeof LedgerKind];

/** Kinds an admin may post by hand. Everything else is system-generated. */
export const MANUAL_LEDGER_KINDS: LedgerKind[] = [
  LedgerKind.Deposit,
  LedgerKind.Adjustment,
];

export interface LedgerEntry {
  id: string;
  traderId: string;
  account: LedgerAccount;
  kind: LedgerKind;
  /** Signed, in micro-USDT. Negative debits the account. */
  amountUsdt: number;
  /** Balance of this account immediately after the entry, for audit replay. */
  balanceAfter: number;
  transactionId: string | null;
  withdrawalId: string | null;
  /** Set on adjustments and on anything an admin caused. */
  reason: string | null;
  createdByAdminId: string | null;
  createdAt: string;
}

export interface TraderBalances {
  /** Collateral free to back new payments. */
  deposit: number;
  /** Collateral committed to unresolved payments. */
  frozen: number;
  /** Earned commission, withdrawable. */
  available: number;
  /** Lifetime withdrawn, for the "Выведено" figure. Derived, not an account. */
  withdrawnLifetime: number;
  /**
   * How much more collateral the trader could commit right now. Equal to
   * `deposit`; named separately because the UI speaks about capacity, not
   * about an account.
   */
  routableCapacity: number;
}

// ---------- Withdrawals ----------

export const WithdrawalStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Sent: 'sent',
  Rejected: 'rejected',
  Failed: 'failed',
} as const;
export type WithdrawalStatus = (typeof WithdrawalStatus)[keyof typeof WithdrawalStatus];

export interface WithdrawalRequest {
  sourceAccount?: 'deposit' | 'available';
  custodyEnabled?: boolean;
  id: string;
  traderId: string;
  traderEmail: string;
  amountUsdt: number;
  /** TRC-20 address. Validated on submission, never mutated afterwards. */
  address: string;
  status: WithdrawalStatus;
  /** On-chain reference once an admin marks it sent. */
  txHash: string | null;
  rejectionReason: string | null;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

/** TRC-20: base58, starts with T, 34 characters. */
export const TRC20_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

export function isValidTrc20Address(value: string): boolean {
  return TRC20_ADDRESS_PATTERN.test(value.trim());
}

// ---------- Commission ----------

export const PaymentMethod = {
  Card: 'card',
  Sbp: 'sbp',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  card: 'Карта',
  sbp: 'СБП',
};

/**
 * Basis points, so a rate is an integer and never a float. 250 = 2.50%.
 * Money maths on floats is how you get a kopeck that belongs to nobody.
 */
export interface CommissionRate {
  method: PaymentMethod;
  /** Trader's cut of the payment, in basis points. */
  rateBps: number;
}

export const BPS_DIVISOR = 10_000;

/** Commission in micro-USDT on a payment worth `amountUsdt`. Floors — the
 *  remainder stays with the platform rather than being invented. */
export function commissionFor(amountUsdt: number, rateBps: number): number {
  return Math.floor((amountUsdt * rateBps) / BPS_DIVISOR);
}

/** 250 -> "2,50%" */
export function formatBps(rateBps: number): string {
  return `${(rateBps / 100).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

// ---------- Per-card limits ----------

export interface CardLimits {
  /** Rolling 24h ceiling in kopecks. 0 means unlimited. */
  dailyLimitRub: number;
  /** Ceiling for a single payment, in kopecks. 0 means unlimited. */
  perPaymentLimitRub: number;
  /** How many unresolved payments may target this card at once. */
  concurrentLimit: number;
}

export interface CardUsage {
  /** Kopecks routed to this card in the last 24h. */
  usedRub24h: number;
  /** Unresolved payments currently targeting this card. */
  activeCount: number;
}

export const DEFAULT_CARD_LIMITS: CardLimits = {
  dailyLimitRub: 0, // No default daily amount cap
  perPaymentLimitRub: 0,
  concurrentLimit: 0,
};

/**
 * Why a card cannot take a payment right now. `null` means it can.
 *
 * Routing and the UI must agree on this, so the reason is computed once here
 * and both sides call it rather than each writing their own version.
 */
export type CardBlockReason =
  | 'not_verified'
  | 'disabled'
  | 'daily_limit'
  | 'concurrent_limit'
  | 'per_payment_limit';

export function cardBlockReason(params: {
  isVerified: boolean;
  isEnabled: boolean;
  limits: CardLimits;
  usage: CardUsage;
  amountRub?: number;
}): CardBlockReason | null {
  const { isVerified, isEnabled, limits, usage, amountRub } = params;

  if (!isVerified) return 'not_verified';
  if (!isEnabled) return 'disabled';
  if (limits.concurrentLimit > 0 && usage.activeCount >= limits.concurrentLimit) return 'concurrent_limit';

  if (amountRub !== undefined) {
    if (limits.dailyLimitRub > 0 && usage.usedRub24h + amountRub > limits.dailyLimitRub) {
      return 'daily_limit';
    }
  }

  return null;
}

// ---------- Why a trader is not receiving traffic ----------

/**
 * Ordered by which the trader should fix first. The API returns the first
 * blocking reason so both consoles explain the same thing.
 */
export type TrafficBlockReason =
  | 'kyc_not_approved'
  | 'traffic_disabled'
  | 'no_usable_card'
  | 'insufficient_collateral';

export interface TrafficStatus {
  /** The trader's own on/off switch. */
  isEnabled: boolean;
  /** True only when nothing at all is blocking routing. */
  canReceive: boolean;
  blockedBy: TrafficBlockReason | null;
  /** Collateral free to back new payments, in micro-USDT. */
  routableCapacity: number;
}
